import React, { useState } from 'react'
import type { DemoBackground } from '../types'
import { ImageIcon } from 'lucide-react'

interface BackgroundEditorProps {
  background?: DemoBackground
  onSetBackground: (bg: DemoBackground) => void
}

const SOLID_PRESETS = [
  { name: 'White', value: '#ffffff' },
  { name: 'Snow', value: '#f8fafc' },
  { name: 'Slate', value: '#f1f5f9' },
  { name: 'Zinc', value: '#18181b' },
  { name: 'Charcoal', value: '#0f172a' },
  { name: 'Obsidian', value: '#09090b' },
  { name: 'Navy', value: '#0c1222' },
  { name: 'Deep Purple', value: '#1a0533' },
]

const GRADIENT_PRESETS = [
  // Light
  { name: 'Cloud', value: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' },
  { name: 'Peach', value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
  // Dark / Electric
  { name: 'Midnight', value: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' },
  { name: 'Dark Ocean', value: 'linear-gradient(135deg, #141e30 0%, #243b55 100%)' },
  { name: 'Cyber', value: 'linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #2d1b69 100%)' },
  { name: 'Electric', value: 'linear-gradient(135deg, #0a0a1a 0%, #1e0533 50%, #0a192f 100%)' },
  { name: 'Neon Glow', value: 'linear-gradient(135deg, #0d0d2b 0%, #1a0a2e 40%, #0a1628 100%)' },
  { name: 'Aurora', value: 'linear-gradient(135deg, #0c1445 0%, #1b0e3d 40%, #051937 100%)' },
  // Vibrant
  { name: 'Sunset', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { name: 'Ocean', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { name: 'Warm', value: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
  { name: 'Aqua', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
]

const ANIMATED_PRESETS = [
  { name: 'Gradient Shift', value: 'bg-animated-gradient' },
  { name: 'Dark Aurora', value: 'bg-animated-dark-aurora' },
  { name: 'Neon Pulse', value: 'bg-animated-neon-pulse' },
  { name: 'Cyber Grid', value: 'bg-animated-cyber-grid' },
  { name: 'Dark Waves', value: 'bg-animated-dark-waves' },
  { name: 'Electric Storm', value: 'bg-animated-electric-storm' },
  { name: 'Mesh Dark', value: 'bg-animated-mesh' },
  { name: 'Mesh Light', value: 'bg-animated-mesh-light' },
  { name: 'Dots Light', value: 'bg-animated-dots' },
  { name: 'Dots Dark', value: 'bg-animated-dots-dark' },
  { name: 'Grid Light', value: 'bg-animated-grid' },
  { name: 'Grid Dark', value: 'bg-animated-grid-dark' },
  { name: 'Glass Light', value: 'bg-animated-glass' },
  { name: 'Glass Dark', value: 'bg-animated-glass-dark' },
]

const ANIMATED_PREVIEWS: Record<string, React.CSSProperties> = {
  'bg-animated-gradient': { background: 'linear-gradient(270deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)' },
  'bg-animated-dark-aurora': { background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)' },
  'bg-animated-neon-pulse': { background: 'linear-gradient(135deg, #0a0a1a, #1a0533, #0a192f)' },
  'bg-animated-cyber-grid': { background: 'linear-gradient(135deg, #0d1117, #161b22, #0d1117)' },
  'bg-animated-dark-waves': { background: 'linear-gradient(135deg, #0c1445, #1b0e3d, #051937)' },
  'bg-animated-electric-storm': { background: 'linear-gradient(135deg, #0f0f23, #1e0533, #0a1628)' },
  'bg-animated-mesh': { background: 'radial-gradient(at 20% 20%, #5048e5, transparent 50%), radial-gradient(at 80% 20%, #ec4899, transparent 50%), radial-gradient(at 50% 80%, #06b6d4, transparent 50%), #0f172a' },
  'bg-animated-mesh-light': { background: 'radial-gradient(at 20% 30%, #c4b5fd, transparent 50%), radial-gradient(at 80% 20%, #fbcfe8, transparent 50%), radial-gradient(at 50% 80%, #a5f3fc, transparent 50%), #f8fafc' },
  'bg-animated-dots': { background: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px) 0 0 / 8px 8px, #f8fafc' },
  'bg-animated-dots-dark': { background: 'radial-gradient(circle, #334155 1px, transparent 1px) 0 0 / 8px 8px, #0f172a' },
  'bg-animated-grid': { background: 'linear-gradient(#e2e8f0 1px, transparent 1px) 0 0 / 12px 12px, linear-gradient(90deg, #e2e8f0 1px, transparent 1px) 0 0 / 12px 12px, #f8fafc' },
  'bg-animated-grid-dark': { background: 'linear-gradient(#1e293b 1px, transparent 1px) 0 0 / 12px 12px, linear-gradient(90deg, #1e293b 1px, transparent 1px) 0 0 / 12px 12px, #0f172a' },
  'bg-animated-glass': { background: 'linear-gradient(135deg, rgba(255,255,255,0.8), rgba(255,255,255,0.4))' },
  'bg-animated-glass-dark': { background: 'linear-gradient(135deg, rgba(15,23,42,0.85), rgba(15,23,42,0.6))' },
}

function PropertyLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
      {children}
    </label>
  )
}

function isSelected(background: DemoBackground | undefined, type: string, value: string): boolean {
  return background?.type === type && background.value === value
}

export default function BackgroundEditor({ background, onSetBackground }: BackgroundEditorProps) {
  return (
    <div className="space-y-5">
      {/* Solid Colors */}
      <div className="space-y-2">
        <PropertyLabel>Solid</PropertyLabel>
        <div className="flex flex-wrap gap-2">
          {SOLID_PRESETS.map((preset) => (
            <button
              key={preset.name}
              title={preset.name}
              onClick={() => onSetBackground({ type: 'solid', value: preset.value })}
              className={`size-8 rounded-lg border-2 transition-all ${
                isSelected(background, 'solid', preset.value)
                  ? 'border-[#5048e5] ring-2 ring-[#5048e5]/20 scale-110'
                  : 'border-slate-200 hover:border-slate-300 hover:scale-105'
              }`}
              style={{ backgroundColor: preset.value }}
            />
          ))}
        </div>
      </div>

      {/* Gradients */}
      <div className="space-y-2">
        <PropertyLabel>Gradients</PropertyLabel>
        <div className="flex flex-wrap gap-2">
          {GRADIENT_PRESETS.map((preset) => (
            <button
              key={preset.name}
              title={preset.name}
              onClick={() => onSetBackground({ type: 'gradient', value: preset.value })}
              className={`size-8 rounded-lg border-2 transition-all ${
                isSelected(background, 'gradient', preset.value)
                  ? 'border-[#5048e5] ring-2 ring-[#5048e5]/20 scale-110'
                  : 'border-slate-200 hover:border-slate-300 hover:scale-105'
              }`}
              style={{ background: preset.value }}
            />
          ))}
        </div>
      </div>

      {/* Custom Image */}
      <div className="space-y-2">
        <PropertyLabel>Image</PropertyLabel>
        <div className="flex gap-2">
          <input
            type="text"
            value={background?.type === 'image' ? background.value : ''}
            onChange={(e) => onSetBackground({ type: 'image', value: e.target.value })}
            placeholder="https://... (image URL)"
            className={`flex-1 bg-slate-50 border rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none transition-all ${
              background?.type === 'image' ? 'border-[#5048e5]' : 'border-slate-200'
            }`}
          />
        </div>
        {background?.type === 'image' && background.value && (
          <div
            className="w-full h-16 rounded-lg border border-slate-200 bg-cover bg-center"
            style={{ backgroundImage: `url(${background.value})` }}
          />
        )}
      </div>

      {/* Animated */}
      <div className="space-y-2">
        <PropertyLabel>Animated</PropertyLabel>
        <div className="grid grid-cols-2 gap-2">
          {ANIMATED_PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => onSetBackground({ type: 'animated', value: preset.value })}
              className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all ${
                isSelected(background, 'animated', preset.value)
                  ? 'border-[#5048e5] ring-2 ring-[#5048e5]/20'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div
                className="w-full h-6 rounded"
                style={ANIMATED_PREVIEWS[preset.value] || {}}
              />
              <span className="text-[10px] font-medium text-slate-500">{preset.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
